import { zodResolver } from '@hookform/resolvers/zod';
import {
  FoodPreferenceSchema,
  GenderSchema,
  HealthContextSchema,
  type UpdateBodyStatsInput,
} from '@onflow/shared';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useOnboardingProfile, useUpdateBodyStats } from '../../hooks/useOnboarding.js';
import { toast } from '../../lib/toast.js';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

const HEALTH_CONTEXT_OPTIONS = [
  { value: 'none', label: 'None of these' },
  { value: 'pregnant', label: 'Pregnant' },
  { value: 'postpartum', label: 'Postpartum recovery' },
  { value: 'medical_supervision', label: 'Following clinician-directed diet or training advice' },
] as const;

const FOOD_PREFERENCE_OPTIONS = [
  { value: 'non_vegetarian', label: 'Non-vegetarian' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'eggetarian', label: 'Eggetarian (vegetarian + eggs)' },
  { value: 'pescetarian', label: 'Pescetarian (vegetarian + fish)' },
] as const;

const BodyStatsFormSchema = z.object({
  age: z
    .number({ invalid_type_error: 'Enter your age' })
    .int('Whole number please')
    .min(16, 'Must be at least 16'),
  heightCm: z
    .number({ invalid_type_error: 'Enter your height in cm' })
    .min(40, 'Must be at least 40 cm')
    .max(250, 'Must be at most 250 cm'),
  gender: GenderSchema,
  healthContext: HealthContextSchema,
  foodPreference: FoodPreferenceSchema.optional(),
});

type BodyStatsFormValues = z.infer<typeof BodyStatsFormSchema>;

export function BodyStatsScreen() {
  const navigate = useNavigate();
  const updateBodyStats = useUpdateBodyStats();
  const profile = useOnboardingProfile();
  const nutritionEnabled = (profile.data?.trackingModes ?? ['nutrition', 'training']).includes(
    'nutrition',
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BodyStatsFormValues>({
    resolver: zodResolver(BodyStatsFormSchema),
    defaultValues: {
      age: profile.data?.age ?? undefined,
      heightCm: profile.data?.heightCm ?? undefined,
      gender: profile.data?.gender ?? undefined,
      healthContext:
        (profile.data?.onboardingData as { healthContext?: BodyStatsFormValues['healthContext'] })
          ?.healthContext ?? undefined,
      foodPreference: profile.data?.foodPreference ?? undefined,
    },
  });

  async function onValidSubmit(values: BodyStatsFormValues) {
    if (nutritionEnabled && !values.foodPreference) {
      toast.error('Choose a food preference to continue');
      return;
    }
    const payload: UpdateBodyStatsInput = {
      age: values.age,
      heightCm: values.heightCm,
      gender: values.gender,
      healthContext: values.healthContext,
      ...(values.foodPreference ? { foodPreference: values.foodPreference } : {}),
    };

    try {
      await updateBodyStats.mutateAsync(payload);
      navigate('/onboarding/activity');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save body stats');
    }
  }

  const saving = updateBodyStats.isPending;

  return (
    <section className="onboarding-screen">
      <form
        className="onboarding-card onboarding-form"
        onSubmit={handleSubmit(onValidSubmit)}
        noValidate
      >
        <h2 className="onboarding-title">Tell us about your body</h2>
        <p className="onboarding-subtitle">We use these to size your plan.</p>

        <div className="form-group">
          <label htmlFor="body-age">Age</label>
          <input
            id="body-age"
            type="number"
            min={16}
            step={1}
            inputMode="numeric"
            placeholder="30"
            aria-invalid={!!errors.age}
            {...register('age', { valueAsNumber: true })}
          />
          {errors.age && (
            <span role="alert" className="form-error">
              {errors.age.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="body-height">Height (cm)</label>
          <input
            id="body-height"
            type="number"
            min={40}
            max={250}
            step={0.1}
            inputMode="decimal"
            placeholder="175"
            aria-invalid={!!errors.heightCm}
            {...register('heightCm', { valueAsNumber: true })}
          />
          {errors.heightCm && (
            <span role="alert" className="form-error">
              {errors.heightCm.message}
            </span>
          )}
        </div>

        <fieldset className="form-group radio-group" aria-invalid={!!errors.gender}>
          <legend className="form-legend">Gender</legend>
          {GENDER_OPTIONS.map((opt) => (
            <label key={opt.value} className="radio-option">
              <input type="radio" value={opt.value} {...register('gender')} />
              <span>{opt.label}</span>
            </label>
          ))}
          {errors.gender && (
            <span role="alert" className="form-error">
              Pick one to continue.
            </span>
          )}
        </fieldset>

        <fieldset className="form-group radio-group" aria-invalid={!!errors.healthContext}>
          <legend className="form-legend">Health context</legend>
          <p className="form-sublabel">
            Automated calorie targets are not appropriate during pregnancy, postpartum recovery, or
            clinician-directed treatment.
          </p>
          {HEALTH_CONTEXT_OPTIONS.map((opt) => (
            <label key={opt.value} className="radio-option">
              <input type="radio" value={opt.value} {...register('healthContext')} />
              <span>{opt.label}</span>
            </label>
          ))}
          {errors.healthContext && (
            <span role="alert" className="form-error">
              Confirm whether any option applies.
            </span>
          )}
        </fieldset>

        {nutritionEnabled && (
          <fieldset className="form-group radio-group" aria-invalid={!!errors.foodPreference}>
            <legend className="form-legend">Food preference</legend>
            {FOOD_PREFERENCE_OPTIONS.map((opt) => (
              <label key={opt.value} className="radio-option">
                <input type="radio" value={opt.value} {...register('foodPreference')} />
                <span>{opt.label}</span>
              </label>
            ))}
            {errors.foodPreference && (
              <span role="alert" className="form-error">
                Pick one to continue.
              </span>
            )}
          </fieldset>
        )}

        <div className="onboarding-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/onboarding/goal')}
          >
            Back
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Next'}
          </button>
        </div>
      </form>
    </section>
  );
}
